import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../../i18n/useP';
import { EditableCell } from '../../components/EditableCell';
import {
  listCostPools,
  createCostPool,
  patchCostPool,
  deleteCostPool,
} from '../../api/tdabc/cost_pools';
import PageHeader from '../../components/PageHeader';
import './CostPoolsPage.css';

function errorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

export default function CostPoolsPage({ activeTenant, addToast, embedded, onPoolsChanged }) {
  const { t } = useTranslation();
  const p = useP();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);

  // Add form state
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newIsOverhead, setNewIsOverhead] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!activeTenant) { setPools([]); return; }
    setLoading(true);
    listCostPools(activeTenant.id)
      .then(res => setPools(res.data || []))
      .catch(err => addToast(errorMessage(err, p('Échec du chargement des groupes ressources')), 'error'))
      .finally(() => setLoading(false));
  }, [activeTenant]); // eslint-disable-line

  function resetAddForm() {
    setNewName('');
    setNewIsOverhead(false);
    setNewDescription('');
    setShowAdd(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const res = await createCostPool(activeTenant.id, {
        name,
        is_overhead_pool: newIsOverhead,
        allocation_driver: newIsOverhead ? 'practical_capacity' : null,
        description: newDescription.trim() || null,
      });
      if (res.data) {
        setPools(prev => [...prev, res.data]);
        resetAddForm();
        onPoolsChanged?.();
      } else {
        addToast(res.error || p('Échec de la création du groupe ressources'), 'error');
      }
    } catch (err) {
      addToast(errorMessage(err, p('Échec de la création du groupe ressources')), 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handlePatch(id, patch) {
    try {
      const res = await patchCostPool(activeTenant.id, id, patch);
      if (res.data) {
        setPools(prev => prev.map(pool => pool.id === id ? res.data : pool));
        onPoolsChanged?.();
      } else {
        addToast(res.error || p('Échec de l’enregistrement'), 'error');
      }
    } catch (err) {
      addToast(errorMessage(err, p('Échec de l’enregistrement')), 'error');
    }
  }

  async function handleDelete(pool) {
    if (!window.confirm(`${p('Supprimer le groupe ressources « ')}${pool.name} » ?`)) return;
    try {
      await deleteCostPool(activeTenant.id, pool.id);
      setPools(prev => prev.filter(row => row.id !== pool.id));
      onPoolsChanged?.();
    } catch (err) {
      addToast(errorMessage(err, p('Échec de la suppression du groupe ressources')), 'error');
    }
  }

  function handleToggleOverhead(pool) {
    handlePatch(pool.id, { is_overhead_pool: !pool.is_overhead_pool });
  }

  if (!activeTenant) {
    return (
      <main className={`page-table-layout cp-layout${embedded ? ' cp-layout--embedded' : ''}`}>
        <div className="page-table-empty">{p('Sélectionner une entreprise.')}</div>
      </main>
    );
  }

  // Direction & Administration : groupes protégés affichés EN TÊTE (fond grisé) ;
  // description par défaut affichée si vide (display, éditable pour persister).
  const DEFAULT_POOL_DESC = {
    direction: p('Membres de la direction, non affectables à un groupe de compétence spécifique.'),
    administration: p('Équipe support sans travail productif facturable (administration, comptabilité, paie, etc.).'),
  };
  const poolDesc = (pool) => pool.description || (pool.is_protected ? (DEFAULT_POOL_DESC[(pool.name || '').trim().toLowerCase()] || '') : '');
  /* Ordre de lecture : la STRUCTURE d'abord — c'est elle qui se réaffecte sur
     les autres, on la lit avant ce qu'elle charge. Administration et Direction
     ouvrent la liste (les deux groupes protégés, présents chez tout le monde),
     puis les autres groupes de structure, puis les groupes directs. À rang
     égal, l'ordre alphabétique. */
  const HEAD = ['administration', 'direction'];
  const rank = (q) => {
    const i = HEAD.indexOf((q.name || '').trim().toLowerCase());
    if (i >= 0) return i;                    // 0, 1
    return q.is_overhead_pool ? 2 : 3;       // structure, puis direct
  };
  const sortedPools = [...pools].sort(
    (a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '', 'fr'),
  );

  return (
    <main className={`page-table-layout cp-layout${embedded ? ' cp-layout--embedded' : ''}`}>
      {!embedded && <PageHeader title={p('Groupes ressources')} />}

      {loading && <p className="page-table-loading">{t('common:status.loading')}</p>}

      {!loading && (
        <div className="page-table-wrap">
          <table className="page-table">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th>{p('Nom')}</th>
                <th>{p('Type')}</th>
                <th>{p('Inducteur')}</th>
                <th>{p('Description')}</th>
                <th style={{ width: 120, textAlign: 'right' }}>{p('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pools.length === 0 && (
                <tr>
                  <td colSpan={6} className="page-table-empty">
                    {p('Aucun groupe ressources — ajoutez-en un ci-dessous.')}
                  </td>
                </tr>
              )}
              {sortedPools.map(pool => (
                <tr key={pool.id} className={pool.is_protected ? 'cp-row--protected' : ''}>
                  <td className="cp-lock-cell">
                    {pool.is_protected
                      ? <span className="cp-lock" title={t('phrases:p_c99bb10b36')}>🔒</span>
                      : null}
                  </td>
                  <td>
                    <EditableCell
                      value={pool.name}
                      field="name"
                      onSave={patch => handlePatch(pool.id, patch)}
                    />
                  </td>
                  <td>
                    <button
                      className={`cp-type-toggle${pool.is_overhead_pool ? ' cp-type-toggle--overhead' : ''}`}
                      onClick={() => handleToggleOverhead(pool)}
                      title={p('Basculer structure / direct')}
                    >
                      {pool.is_overhead_pool ? p('Structure') : p('Direct')}
                    </button>
                  </td>
                  <td className="cp-driver-cell">
                    {pool.is_overhead_pool
                      ? <span className="cp-driver-fixed">{p('Capacité pratique')}</span>
                      : <span className="cp-driver-na">{p('— s/o')}</span>}
                  </td>
                  <td>
                    <EditableCell
                      value={poolDesc(pool)}
                      field="description"
                      placeholder={p('Saisir une description')}
                      onSave={patch => handlePatch(pool.id, patch)}
                    />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {pool.is_protected ? (
                      <span className="cp-protected-label">{p('protégé')}</span>
                    ) : (
                      <button
                        className="p-btn-delete"
                        onClick={() => handleDelete(pool)}
                      >
                        {p('Supprimer')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="cp-add-row">
                <td colSpan={6}>
                  {showAdd ? (
                    <form className="cp-add-form" onSubmit={handleCreate}>
                      <input
                        className="cp-add-name"
                        placeholder={t('phrases:p_d6c5a0e034')}
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        disabled={creating}
                        autoFocus
                      />
                      <input
                        className="cp-add-description"
                        placeholder={p('Description (facultative)')}
                        value={newDescription}
                        onChange={e => setNewDescription(e.target.value)}
                        disabled={creating}
                      />
                      <label className="cp-add-checkbox">
                        <input
                          type="checkbox"
                          checked={newIsOverhead}
                          onChange={e => setNewIsOverhead(e.target.checked)}
                          disabled={creating}
                        />
                        <span>{p('Groupe de structure')}</span>
                      </label>
                      <button className="p-btn p-btn--primary" type="submit" disabled={!newName.trim() || creating}>
                        {creating ? '…' : p('Créer')}
                      </button>
                      <button className="p-btn" type="button" onClick={resetAddForm} disabled={creating}>
                        {t('common:actions.cancel')}
                      </button>
                    </form>
                  ) : (
                    <button className="cp-add-row-btn" onClick={() => setShowAdd(true)}>
                      {p('+ Ajouter un groupe')}
                    </button>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </main>
  );
}
